import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CreatableCombobox } from "../ui/creatable-combobox";
import { useAudit } from "../../contexts/auditContext";
import { useFyCycleField } from "../../hooks/useFyCycleField";
import { TestPhases, TestPhase } from "../../models/audit/controlSession";
import { SetDefaultFyCycleDialog } from "./SetDefaultFyCycleDialog";

interface EditTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditTestDialog({ open, onOpenChange }: EditTestDialogProps) {
  const {
    controlSession,
    sessionKind,
    updateControlSessionName,
    updateControlSessionPhase,
    updateControlSessionFyCycle,
  } = useAudit();
  // Test phase / FY cycle are control-session concepts; work step sessions
  // only carry a name, so the dialog collapses to a rename for them.
  const isWorkStepSession = sessionKind === "work_step";
  const {
    fyCycle,
    fyCycleOptionNames,
    pendingDefaultName,
    handleChange: handleFyCycleChange,
    setFyCycle,
    confirmSetDefault,
    dismissSetDefault,
    resolveFyCycleId,
  } = useFyCycleField();

  const [name, setName] = useState("");
  const [testPhase, setTestPhase] = useState<TestPhase>("not_defined");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && controlSession) {
      setName(controlSession.name ?? "");
      setTestPhase(controlSession.testPhase ?? "not_defined");
      setFyCycle(controlSession.fyCycle?.name ?? "");
    }
  }, [open, controlSession, setFyCycle]);

  const handleSave = async () => {
    if (!controlSession) return;
    setIsSaving(true);
    try {
      const trimmedName = name.trim();
      if (trimmedName && trimmedName !== controlSession.name) {
        await updateControlSessionName(trimmedName);
      }
      if (
        !isWorkStepSession &&
        testPhase !== (controlSession.testPhase ?? "not_defined")
      ) {
        await updateControlSessionPhase(testPhase);
      }
      const currentFyName = controlSession.fyCycle?.name ?? "";
      if (!isWorkStepSession && fyCycle !== currentFyName) {
        const fyCycleId = await resolveFyCycleId();
        await updateControlSessionFyCycle(fyCycleId);
      }
      onOpenChange(false);
    } catch {
      // Underlying calls have already toasted; keep the dialog open so the
      // user can correct the field and retry.
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = !!name.trim() && !isSaving;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-test-name">Name</Label>
              <Input
                id="edit-test-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
              />
            </div>
            {!isWorkStepSession && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-test-phase">Test Phase</Label>
                  <Select
                    value={testPhase}
                    onValueChange={(value) => {
                      const phase = TestPhases.find((p) => p.value === value);
                      if (phase) setTestPhase(phase.value);
                    }}
                    disabled={isSaving}
                  >
                    <SelectTrigger id="edit-test-phase">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TestPhases.map((phase) => (
                        <SelectItem key={phase.value} value={phase.value}>
                          {phase.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>FY</Label>
                  <CreatableCombobox
                    value={fyCycle}
                    onChange={handleFyCycleChange}
                    options={fyCycleOptionNames}
                    testId="edit-test-fy-cycle-combobox"
                    placeholder="Select or create..."
                    inputPlaceholder="Create new FY cycle..."
                    emptyText="No FY cycles yet. Type to create one."
                    createText="Create"
                    disabled={isSaving}
                    modal
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave} loading={isSaving}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SetDefaultFyCycleDialog
        open={pendingDefaultName !== null}
        optionName={pendingDefaultName ?? ""}
        onConfirm={confirmSetDefault}
        onDismiss={dismissSetDefault}
      />
    </>
  );
}
